import dayjs, { Dayjs } from 'dayjs';

import { ExcalidrawInitialDataState } from '@excalidraw/excalidraw/dist/types/excalidraw/types';
import { Transcript } from './transcripts';

export type Session = {
  id: number;
  name: string;
  status: string;
  createdAt: Dayjs;
  updatedAt: Dayjs;
  patientId: number;
  transcript: null | Transcript;
  transcriptNormalized: null | Transcript;
  report: null | string;
  sourceType: number;
  hasError: boolean;
  errorCode: null | number;
  errorMessage: null | string;
  excalidrawContent?: Object;
  excalidrawJson?: ExcalidrawInitialDataState;
};

export type LocaleString = {
  es: string;
  en: string;
};

export type SessionTemplate = {
  name: string;
  description: LocaleString;
  fileName: string;
  preview: string;
};

export interface SessionsMixin {
  fetch: (path: string, init?: RequestInit) => Promise<any>;
  sessionsList: (patientId: number) => Promise<Session[]>;
  sessionsGet: (id: number) => Promise<Session>;
  sessionsCreate: (
    patientId: number,
    meetingUrl: string,
    source: number,
    hasGraphicNotes?: boolean,
    excalidrawFile?: any
  ) => Promise<Session>;
  sessionUpdate: (id: number, session: Session, updatedJSON?: any) => Promise<Session>;
  sessionsDestroy: (id: number) => Promise<undefined>;
  hasNotes: (id: number) => Promise<Boolean>;
}
export const SessionsMixin = {
  async fetch(path: string, init?: RequestInit): Promise<any> {
    throw new Error('Not implemented');
  },
  async sessionsList(patientId: number): Promise<Session[]> {
    const sessions = await this.fetch(`/sessions?patientId=${patientId}`);
    for (const session of sessions) {
      session.createdAt = dayjs(session.createdAt);
      session.updatedAt = dayjs(session.updatedAt);
    }
    return sessions;
  },
  async sessionsGet(id: number): Promise<Session> {
    const sess = (await this.fetch(`/sessions/${id}`)) as Session;
    sess.createdAt = dayjs(sess.createdAt);
    sess.updatedAt = dayjs(sess.updatedAt);
    if (sess.transcript) {
      for (const item of sess.transcript) {
        for (const words of item.words) {
          words.startTimestamp = sess.createdAt.add(words.startTimestamp as any as number, 's');
          words.endTimestamp = sess.createdAt.add(words.endTimestamp as any as number, 's');
        }
      }
    }
    return sess;
  },
  async sessionsCreate(
    patientId: number,
    meetingUrl: string,
    source = 0,
    hasGraphicNotes = false,
    excalidrawFile = null
  ): Promise<Session> {
    return this.fetch('/sessions', {
      method: 'post',
      body: JSON.stringify({
        meetingUrl,
        patientId,
        source,
        excalidrawFile,
      }),
    });
  },
  async sessionUpdate(id: number, session: Session, updatedJSON?: any): Promise<Session> {
    const sess = (await this.fetch(`/sessions/${id}`, {
      method: 'put',
      body: JSON.stringify({
        ...session,
      }),
    })) as Session;
    return sess;
  },
  async sessionsDestroy(id: number): Promise<undefined> {
    await this.fetch(`/sessions/${id}`, {
      method: 'DELETE',
    });
  },
  async hasNotes(id: number): Promise<Boolean> {
    const response = await this.fetch(`/sessions/${id}/has-notes`, {
      method: 'get',
    });
    return response.exist;
  },
} as SessionsMixin;
